import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Phone, MessageCircle, ArrowRight, ArrowLeft, Lock } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
export interface LeadInfo {
  name: string;
  phone: string;
  line: string;
}
interface LeadFormStepProps {
  onSubmit: (info: LeadInfo) => void;
  onBack: () => void;
}
export function LeadFormStep({
  onSubmit,
  onBack
}: LeadFormStepProps) {
  const {
    t
  } = useLanguage();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [line, setLine] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    phone?: string;
    consent?: string;
  }>({});
  const validate = () => {
    const next: typeof errors = {};
    if (!name.trim()) next.name = t('faceAnalysis.lead.errorName');
    const phoneDigits = phone.replace(/[^0-9]/g, '');
    if (phoneDigits.length < 8) next.phone = t('faceAnalysis.lead.errorPhone');
    if (!consent) next.consent = t('faceAnalysis.lead.errorConsent');
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        name: name.trim(),
        phone: phone.trim(),
        line: line.trim()
      });
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-lg mx-auto bg-white rounded-2xl shadow-xl shadow-brand-copper/5 border border-brand-lightCopper/30 overflow-hidden" data-id="element-326">
      <div className="p-8 md:p-10" data-id="element-327">
        <div className="text-center mb-8" data-id="element-328">
          <div className="w-16 h-16 bg-brand-copper/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-329">
            <User className="w-8 h-8 text-brand-copper" data-id="element-330" />
          </div>
          <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-2" data-id="element-331">
            {t('faceAnalysis.lead.title')}
          </h2>
          <p className="text-gray-600 text-sm" data-id="element-332">
            {t('faceAnalysis.lead.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" noValidate data-id="element-333">
          {/* Name */}
          <div data-id="element-334">
            <label htmlFor="lead-name" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-335">
              {t('faceAnalysis.lead.name')}{' '}
              <span className="text-red-500" data-id="element-336">*</span>
            </label>
            <div className="relative" data-id="element-337">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-338" />
              <input id="lead-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder={t('faceAnalysis.lead.namePlaceholder')} className={`w-full pl-10 pr-4 py-3 rounded-xl border bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-copper/30 transition-colors ${errors.name ? 'border-red-300' : 'border-gray-200'}`} aria-invalid={!!errors.name} data-id="element-339" />
            </div>
            {errors.name && <p className="text-xs text-red-500 mt-1" data-id="element-340">{errors.name}</p>}
          </div>

          {/* Phone */}
          <div data-id="element-341">
            <label htmlFor="lead-phone" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-342">
              {t('faceAnalysis.lead.phone')}{' '}
              <span className="text-red-500" data-id="element-343">*</span>
            </label>
            <div className="relative" data-id="element-344">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-345" />
              <input id="lead-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('faceAnalysis.lead.phonePlaceholder')} className={`w-full pl-10 pr-4 py-3 rounded-xl border bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-copper/30 transition-colors ${errors.phone ? 'border-red-300' : 'border-gray-200'}`} aria-invalid={!!errors.phone} data-id="element-346" />
            </div>
            {errors.phone && <p className="text-xs text-red-500 mt-1" data-id="element-347">{errors.phone}</p>}
          </div>

          {/* LINE (optional) */}
          <div data-id="element-348">
            <label htmlFor="lead-line" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-349">
              {t('faceAnalysis.lead.line')}
            </label>
            <div className="relative" data-id="element-350">
              <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-351" />
              <input id="lead-line" type="text" value={line} onChange={e => setLine(e.target.value)} placeholder={t('faceAnalysis.lead.linePlaceholder')} className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-copper/30 transition-colors" data-id="element-352" />
            </div>
          </div>

          {/* Consent */}
          <div data-id="element-353">
            <label className="flex items-start cursor-pointer" data-id="element-354">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-copper focus:ring-brand-copper/30" data-id="element-355" />
              <span className="ml-2 text-sm text-gray-600" data-id="element-356">
                {t('faceAnalysis.lead.consent')}
              </span>
            </label>
            {errors.consent && <p className="text-xs text-red-500 mt-1" data-id="element-357">{errors.consent}</p>}
          </div>

          {/* Privacy note */}
          <div className="flex items-start text-xs text-gray-500 bg-brand-bg/50 p-3 rounded-lg" data-id="element-358">
            <Lock className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-brand-copper" data-id="element-359" />
            <p data-id="element-360">{t('faceAnalysis.lead.privacy')}</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2" data-id="element-361">
            <button type="button" onClick={onBack} className="flex items-center justify-center px-5 py-3 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-362">
              <ArrowLeft className="w-4 h-4 mr-1.5" data-id="element-363" />
              {t('faceAnalysis.lead.back')}
            </button>
            <button type="submit" className="flex-1 flex items-center justify-center px-6 py-3 rounded-full bg-brand-copper text-white hover:bg-brand-darkCopper transition-colors font-medium shadow-lg shadow-brand-copper/20" data-id="element-364">
              {t('faceAnalysis.lead.continue')}
              <ArrowRight className="w-4 h-4 ml-2" data-id="element-365" />
            </button>
          </div>
        </form>
      </div>
    </motion.div>;
}